import React, { useState } from "react";
import { ItemTemplate } from "../types";

interface GloryItemSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedItem: ItemTemplate) => void;
  maxGloryCost: number;
  title: string;
  itemTemplates: ItemTemplate[];
}

// Mock glory items for demonstration - in a real implementation,
// these would come from a dedicated glory items data source
const MOCK_GLORY_ITEMS: ItemTemplate[] = [
  {
    id: "glory-1",
    templateName: "Combat Drugs",
    name: "Combat Drugs",
    category: "equipment",
    keywords: ["RARE"],
    range: "",
    armourModifier: "",
    notes: "Once per battle, remove one Battle Scar from a model in your warband.",
    cost: 3,
    costCurrency: "glory"
  },
  {
    id: "glory-2",
    templateName: "Medal of Valour",
    name: "Medal of Valour",
    category: "equipment",
    keywords: ["RELIC"],
    range: "",
    armourModifier: "",
    notes: "Models within 6\" of the bearer gain +1 to Morale checks.",
    cost: 5,
    costCurrency: "glory"
  },
  {
    id: "glory-3",
    templateName: "Blessed Weapon",
    name: "Blessed Weapon",
    category: "melee",
    keywords: ["BLESSED", "RELIC"],
    range: "",
    armourModifier: "",
    notes: "Holy weapon that ignores armour saves against certain enemies.",
    cost: 6,
    costCurrency: "glory"
  },
  {
    id: "glory-4",
    templateName: "Sacred Shield",
    name: "Sacred Shield",
    category: "armour",
    keywords: ["RELIC", "SHIELD"],
    range: "",
    armourModifier: "+1",
    notes: "Provides additional protection and holy wards.",
    cost: 7,
    costCurrency: "glory"
  },
  {
    id: "glory-5",
    templateName: "Holy Relic",
    name: "Holy Relic",
    category: "equipment",
    keywords: ["RELIC", "HOLY"],
    range: "",
    armourModifier: "",
    notes: "Powerful religious artifact with multiple blessings.",
    cost: 10,
    costCurrency: "glory"
  }
];

export const GloryItemSelectionModal = ({
  isOpen,
  onClose,
  onConfirm,
  maxGloryCost,
  title,
  itemTemplates = MOCK_GLORY_ITEMS
}: GloryItemSelectionModalProps) => {
  const [selectedItem, setSelectedItem] = useState<ItemTemplate | null>(null);

  // Filter items by glory cost
  const availableItems = itemTemplates.filter(item =>
    item.costCurrency === "glory" && item.cost <= maxGloryCost
  );

  const handleConfirm = () => {
    if (selectedItem) {
      onConfirm(selectedItem);
      setSelectedItem(null);
    }
  };

  const handleClose = () => {
    setSelectedItem(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
      <div className="ledger-shell max-w-3xl w-full max-h-[80vh] overflow-y-auto text-sm">
        <div className="p-4">
          <h2 className="text-base font-semibold uppercase tracking-wider">{title}</h2>
          <p className="text-xs opacity-90 mt-2">
            Select a Glory Item costing up to {maxGloryCost} ☼
          </p>
        </div>

        <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
          {availableItems.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No glory items available within your cost limit
            </div>
          ) : (
            availableItems.map(item => (
              <div
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className={`p-4 rounded border cursor-pointer transition-colors ${
                  selectedItem?.id === item.id
                    ? 'bg-yellow-900/50 border-yellow-600'
                    : 'bg-gray-900/50 border-gray-600 hover:bg-gray-800/50'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-200">{item.name}</h3>
                      <span className="bg-yellow-900/50 text-yellow-400 px-2 py-0.5 rounded text-xs font-bold border border-yellow-700">
                        {item.cost} ☼
                      </span>
                    </div>
                    <div className="text-sm text-gray-400 mb-2">{item.category}</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1 mb-2">
                  {item.keywords.map((keyword, idx) => (
                    <span key={idx} className="bg-red-950/40 text-red-400 px-2 py-0.5 rounded text-xs uppercase tracking-wider">
                      {keyword}
                    </span>
                  ))}
                </div>

                {(item.range || item.armourModifier) && (
                  <div className="text-xs text-gray-500 space-y-0.5 mb-2">
                    {item.range && <div>RANGE: {item.range}"</div>}
                    {item.armourModifier && <div>ARMOUR: {item.armourModifier}</div>}
                  </div>
                )}

                {item.notes && (
                  <div className="text-xs text-gray-400 italic">
                    {item.notes}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="p-4 border-t border-gray-700 flex gap-2">
          <button
            onClick={handleConfirm}
            disabled={!selectedItem}
            className="flex-1 bg-yellow-900 hover:bg-yellow-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded font-semibold uppercase tracking-wider"
          >
            {selectedItem ? `Select ${selectedItem.name}` : 'Select Item'}
          </button>
          <button
            onClick={handleClose}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded uppercase tracking-wider"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
